import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BillingImportPreview,
  BillingImportResult,
  BillingReceiveInput,
  BillingReceiveResult,
} from '@accounting/shared';
import { api } from '../lib/api';

/**
 * Bateman Capital Management advisory-fee billing import.
 *
 * The company gate lives on the server — these hooks are only ever rendered for
 * BCM, but hiding the UI is presentation, not enforcement.
 */

/** One open invoice offered on the receive step. */
export interface OpenBillingInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  memo: string | null;
  outstandingAmount: string;
  customerId: string | null;
}

export const openBillingKey = (companyId: string | null) =>
  ['billing-import', 'open', companyId ?? 'none'] as const;

/** Read a File as base64 without the data: prefix the API does not want. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/** Parse and validate the CSV. Writes nothing. */
export function useBillingPreview(companyId: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      const contentBase64 = await fileToBase64(file);
      return api
        .post<{ preview: BillingImportPreview }>(`/companies/${companyId}/billing-import/preview`, {
          fileName: file.name,
          contentBase64,
        })
        .then((r) => r.preview);
    },
  });
}

/** Create the invoices. */
export function useBillingCommit(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const contentBase64 = await fileToBase64(file);
      return api
        .post<{ result: BillingImportResult }>(`/companies/${companyId}/billing-import`, {
          fileName: file.name,
          contentBase64,
        })
        .then((r) => r.result);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: openBillingKey(companyId) });
      qc.invalidateQueries({ queryKey: ['accounts', companyId] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
    },
  });
}

export function useOpenBillingInvoices(companyId: string | null, enabled = true) {
  return useQuery({
    queryKey: openBillingKey(companyId),
    enabled: companyId !== null && enabled,
    queryFn: () =>
      api
        .get<{ invoices: OpenBillingInvoice[] }>(`/companies/${companyId}/billing-import/open`)
        .then((r) => r.invoices),
  });
}

export function useReceiveBillingPayments(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BillingReceiveInput) =>
      api
        .post<{ result: BillingReceiveResult }>(
          `/companies/${companyId}/billing-import/receive`,
          input,
        )
        .then((r) => r.result),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: openBillingKey(companyId) });
      qc.invalidateQueries({ queryKey: ['accounts', companyId] });
      qc.invalidateQueries({ queryKey: ['report'] });
      qc.invalidateQueries({ queryKey: ['command-center'] });
    },
  });
}
